import React, { useState, useEffect, useRef } from 'react';
import { auth, db, storage } from '../firebase';
import { doc, onSnapshot, collection, addDoc, serverTimestamp, query, orderBy, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { Pencil, Upload, Eye, Image as ImageIcon, Video, Shield, Users, Lock, Settings, Loader2, PlayCircle, X, Play, Pause, SkipBack, SkipForward, MoreVertical, Trash2, Globe, UserPlus, Star, Edit3, Check, Camera, Save } from 'lucide-react';
import SideNav from '../components/SideNav';
import EditProfileModal from '../components/EditProfileModal';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [mediaItems, setMediaItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // MODALS STATE
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isPhotoSelectorOpen, setIsPhotoSelectorOpen] = useState(false);
  const [photoSelectorTarget, setPhotoSelectorTarget] = useState(null);

  const [isFeaturedEditOpen, setIsFeaturedEditOpen] = useState(false);
  const [featuredEditIndex, setFeaturedEditIndex] = useState(0);
  const [featuredEditText, setFeaturedEditText] = useState('');
  const [featuredEditTitle, setFeaturedEditTitle] = useState(''); // NEW TITLE STATE

  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [featuredMenuOpenId, setFeaturedMenuOpenId] = useState(null);

  const [displayName, setDisplayName] = useState(true);
  const [activeMediaTab, setActiveMediaTab] = useState('PHOTOS');
  const [activePrivacyTab, setActivePrivacyTab] = useState('PRIVATE');

  useEffect(() => {
    // DIAGNOSTIC START
    console.log("Starting Profile Initialization...");

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log("User Authenticated:", user.uid);

        // 1. GET USER DATA
        const docRef = doc(db, 'users', user.uid);
        onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            console.log("✅ Profile Document Found");
            setUserData(docSnap.data());
            setDisplayName(docSnap.data().displayNameEnabled ?? true);
          } else {
            console.error("❌ Profile Document MISSING in Firestore for ID:", user.uid);
            // SAFETY FALLBACK: Load defaults so page doesn't break
            setUserData({
              firstName: "Profile",
              lastName: "Error",
              username: "System",
              tier: "Guest"
            });
          }
        }, (error) => {
          console.error("❌ Profile Read Permission Error:", error);
        });

        // 2. GET MEDIA (This handles the Loader)
        const mediaQuery = query(collection(db, 'users', user.uid, 'media'), orderBy('createdAt', 'desc'));
        onSnapshot(mediaQuery, (snapshot) => {
          console.log("✅ Media Loaded:", snapshot.docs.length, "items");
          setMediaItems(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
          setLoading(false); // OPEN THE GATES (Success)
        }, (error) => {
          console.error("❌ Media Read Error:", error);
          setLoading(false); // OPEN THE GATES (Failure Recovery)
        });

      } else {
        console.log("No user found, redirecting.");
        setLoading(false);
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  const filteredMedia = mediaItems.filter(item => {
    const typeMatch = activeMediaTab === 'PHOTOS' ? item.type === 'IMAGE' : item.type === 'VIDEO';
    return typeMatch && item.privacy === activePrivacyTab;
  });

  const openPhotoSelector = (target) => {
    setPhotoSelectorTarget(target);
    setIsPhotoSelectorOpen(true);
    setMenuOpenId(null);
    setFeaturedMenuOpenId(null);
  };

  const handlePhotoSelect = async (url) => {
    if (photoSelectorTarget === 'PROFILE') {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { profilePic: url });
    } else if (photoSelectorTarget.startsWith('FEATURED_')) {
      const index = parseInt(photoSelectorTarget.split('_')[1]);
      let newFeatured = [...(userData?.featured || ['', '', ''])];
      newFeatured[index] = url;
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { featured: newFeatured });
    }
    setIsPhotoSelectorOpen(false);
  };

  const openFeaturedEdit = (index) => {
    setFeaturedEditIndex(index);
    setFeaturedEditTitle(userData?.featuredTitles?.[index] || ''); // LOAD TITLE
    setFeaturedEditText(userData?.featuredAbout?.[index] || '');   // LOAD ABOUT
    setIsFeaturedEditOpen(true);
    setFeaturedMenuOpenId(null);
  };

  const saveFeaturedEdit = async () => {
    let newTitles = [...(userData?.featuredTitles || ['', '', ''])];
    let newAbout = [...(userData?.featuredAbout || ['', '', ''])];

    newTitles[featuredEditIndex] = featuredEditTitle;
    newAbout[featuredEditIndex] = featuredEditText;

    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      featuredTitles: newTitles,
      featuredAbout: newAbout
    });
    setIsFeaturedEditOpen(false);
  };

  const removeFeatured = async (index) => {
    let newFeatured = [...(userData?.featured || ['', '', ''])];
    let newTitles = [...(userData?.featuredTitles || ['', '', ''])];
    let newAbout = [...(userData?.featuredAbout || ['', '', ''])];

    newFeatured[index] = '';
    newTitles[index] = '';
    newAbout[index] = '';

    await updateDoc(doc(db, 'users', auth.currentUser.uid), {
      featured: newFeatured,
      featuredTitles: newTitles,
      featuredAbout: newAbout
    });
    setFeaturedMenuOpenId(null);
  };

  const moveMedia = async (itemId, newPrivacy) => {
    const mediaRef = doc(db, 'users', auth.currentUser.uid, 'media', itemId);
    await updateDoc(mediaRef, { privacy: newPrivacy });
    setMenuOpenId(null);
  };

  const deleteMedia = async (item) => {
    if (!window.confirm("SHRED FILE PERMANENTLY?")) return;
    await deleteDoc(doc(db, 'users', auth.currentUser.uid, 'media', item.id));
    setMenuOpenId(null);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    uploadFile(file, file.type.startsWith('video/') ? 'VIDEO' : 'IMAGE');
  };

  const uploadFile = (file, type) => {
    setIsUploading(true);
    const fileName = Date.now() + '_' + file.name;
    const storageRef = ref(storage, 'users/' + auth.currentUser.uid + '/private/' + fileName);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on('state_changed',
      (snap) => setUploadProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
      null,
      async () => {
        const url = await getDownloadURL(uploadTask.snapshot.ref);
        await addDoc(collection(db, 'users', auth.currentUser.uid, 'media'), {
          url, type, privacy: 'PRIVATE', createdAt: serverTimestamp()
        });
        setIsUploading(false);
        setActiveMediaTab(type === 'VIDEO' ? 'VIDEO' : 'PHOTOS');
        setActivePrivacyTab('PRIVATE');
      }
    );
  };

  const openPlayer = (video) => { setSelectedVideo(video); setIsPlaying(true); };
  const closePlayer = () => { setSelectedVideo(null); setIsPlaying(false); };
  const togglePlay = () => { videoRef.current.paused ? videoRef.current.play() : videoRef.current.pause(); setIsPlaying(!videoRef.current.paused); };
  const navigateVideo = (dir) => {
    const idx = filteredMedia.findIndex(v => v.id === selectedVideo.id);
    if (filteredMedia[idx + dir]) setSelectedVideo(filteredMedia[idx + dir]);
  };

  if (loading) return <div style={styles.loader}>INITIALIZING...</div>;

  return (
    <div style={styles.layout}>
      <SideNav activePage="/profile" />
      <div style={styles.mainContent}>

        <div style={styles.profileHeader}>
          <div style={styles.picContainer} onMouseEnter={(e) => e.currentTarget.children[1].style.opacity = 1} onMouseLeave={(e) => e.currentTarget.children[1].style.opacity = 0}>
            <img src={userData?.profilePic || 'https://placehold.co/150'} style={styles.profilePic} alt="User" />
            <div style={styles.editOverlay} onClick={() => setEditModalOpen(true)}>
              <Pencil size={18} color="#000" />
            </div>
          </div>

          <div style={styles.infoBlock}>
            <h1 style={styles.name}>{userData?.firstName} {userData?.lastName}</h1>
            <h2 style={styles.username}>@{userData?.username}</h2>
            <div style={styles.rankBadge}><Shield size={12} /> {userData?.tier}</div>

            <div style={styles.featuredRow}>
              {[0, 1, 2].map(i => (
                <div key={i} style={styles.featSlotContainer}>
                  <div style={styles.featSlot}>
                    {userData?.featured?.[i] ? <img src={userData.featured[i]} style={styles.featImg} /> : <ImageIcon size={20} color="#222" />}
                  </div>
                  <button style={styles.featMenuBtn} onClick={() => setFeaturedMenuOpenId(featuredMenuOpenId === i ? null : i)}>
                    <MoreVertical size={14} color="#D4AF37" />
                  </button>
                  {featuredMenuOpenId === i && (
                    <div style={styles.featDropdown}>
                      <button onClick={() => openFeaturedEdit(i)} style={styles.dropItem}><Edit3 size={12} /> EDIT</button>
                      <button onClick={() => openPhotoSelector('FEATURED_' + i)} style={styles.dropItem}><Camera size={12} /> CHANGE</button>
                      <button onClick={() => removeFeatured(i)} style={{ ...styles.dropItem, color: 'red' }}><Trash2 size={12} /> REMOVE</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {isUploading && <div style={styles.progressLabel}>TRANSMITTING: {uploadProgress}%</div>}

        <div style={styles.actionRow}>
          <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleFileSelect} accept="image/*,video/*" />
          <button style={styles.uploadBtn} onClick={() => fileInputRef.current.click()}><Upload size={18} style={{ marginRight: 8 }} /> UPLOAD MEDIA</button>
          <button style={styles.viewLinkBtn} onClick={() => navigate('/profile-view/' + auth.currentUser.uid)}>
            <Eye size={18} style={{ marginRight: 8 }} /> VIEW PROFILE
          </button>
        </div>

        <div style={styles.galleryWrapper}>
          <div style={styles.tabs}>
            <button style={activeMediaTab === 'PHOTOS' ? styles.activeTab : styles.tab} onClick={() => setActiveMediaTab('PHOTOS')}>PHOTOS</button>
            <button style={activeMediaTab === 'VIDEO' ? styles.activeTab : styles.tab} onClick={() => setActiveMediaTab('VIDEO')}>VIDEO LIBRARY</button>
          </div>

          <div style={styles.folderContainer}>
            {['PUBLIC', 'FRIENDS-ONLY', 'PRIVATE'].map(p => (
              <button key={p} style={activePrivacyTab === p ? styles.activeFolder : styles.folder} onClick={() => setActivePrivacyTab(p)}>{p}</button>
            ))}
          </div>

          <div style={styles.grid}>
            {filteredMedia.map(item => (
              <div key={item.id} style={styles.card}>
                {item.type === 'IMAGE' ? <img src={item.url} style={styles.thumb} /> : <div onClick={() => openPlayer(item)} style={styles.vidThumb}><PlayCircle size={40} color="#D4AF37" /></div>}

                <button style={styles.menuBtn} onClick={() => setMenuOpenId(menuOpenId === item.id ? null : item.id)}><MoreVertical size={16} /></button>

                {menuOpenId === item.id && (
                  <div style={styles.dropdown}>
                    <button onClick={() => { setPhotoSelectorTarget('PROFILE'); handlePhotoSelect(item.url); }} style={styles.dropItem}><Pencil size={12} /> SET PROFILE PIC</button>
                    <button onClick={() => { setPhotoSelectorTarget('FEATURED_0'); handlePhotoSelect(item.url); }} style={styles.dropItem}><Star size={12} /> SET FEATURED 1</button>
                    <button onClick={() => { setPhotoSelectorTarget('FEATURED_1'); handlePhotoSelect(item.url); }} style={styles.dropItem}><Star size={12} /> SET FEATURED 2</button>
                    <button onClick={() => { setPhotoSelectorTarget('FEATURED_2'); handlePhotoSelect(item.url); }} style={styles.dropItem}><Star size={12} /> SET FEATURED 3</button>
                    <button onClick={() => moveMedia(item.id, 'PUBLIC')} style={styles.dropItem}><Globe size={12} /> MAKE PUBLIC</button>
                    <button onClick={() => deleteMedia(item)} style={{ ...styles.dropItem, color: 'red' }}><Trash2 size={12} /> SHRED</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {selectedVideo && (
          <div style={styles.playerOverlay}>
            <div style={styles.playerWindow}>
              <button onClick={closePlayer} style={styles.closeBtn}><X /></button>
              <video ref={videoRef} src={selectedVideo.url} autoPlay onTimeUpdate={() => setCurrentTime(videoRef.current.currentTime)} onLoadedMetadata={() => setDuration(videoRef.current.duration)} style={{ width: '100%' }} />
              <div style={styles.playerControls}>
                <input type="range" min="0" max={duration} value={currentTime} onChange={(e) => { videoRef.current.currentTime = e.target.value; setCurrentTime(e.target.value); }} style={{ width: '100%', accentColor: '#D4AF37' }} />
                <div style={styles.btnRow}>
                  <button onClick={() => navigateVideo(1)} style={styles.navBtn}><SkipBack /></button>
                  <button onClick={togglePlay} style={styles.playBtn}>{isPlaying ? <Pause /> : <Play />}</button>
                  <button onClick={() => navigateVideo(-1)} style={styles.navBtn}><SkipForward /></button>
                </div>
              </div>
            </div>
          </div>
        )}

        {isPhotoSelectorOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.selectorWindow}>
              <div style={styles.modalHeader}>
                <h3>SELECT PHOTO</h3>
                <button onClick={() => setIsPhotoSelectorOpen(false)} style={styles.closeIconBtn}><X /></button>
              </div>
              <div style={styles.selectorGrid}>
                {mediaItems.filter(i => i.type === 'IMAGE').map(img => (
                  <img key={img.id} src={img.url} style={styles.selectorThumb} onClick={() => handlePhotoSelect(img.url)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {isFeaturedEditOpen && (
          <div style={styles.modalOverlay}>
            <div style={styles.featEditWindow}>
              <div style={styles.modalHeader}>
                <h3>FEATURED PHOTO EDIT</h3>
                <button onClick={() => setIsFeaturedEditOpen(false)} style={styles.closeIconBtn}><X /></button>
              </div>
              <div style={styles.featEditBody}>
                <div style={styles.featEditLeft}>
                  {/* NEW TITLE INPUT */}
                  <label style={styles.label}>TITLE</label>
                  <input style={styles.titleInput} value={featuredEditTitle} onChange={e => setFeaturedEditTitle(e.target.value)} placeholder="ENTER TITLE..." />

                  <label style={styles.label}>ABOUT</label>
                  <textarea style={styles.textArea} value={featuredEditText} onChange={e => setFeaturedEditText(e.target.value)} placeholder="ENTER DESCRIPTION..." />

                  <button onClick={saveFeaturedEdit} style={styles.saveBtn}><Save size={14} style={{ marginRight: 5 }} /> SAVE</button>
                </div>
                <div style={styles.featEditRight}>
                  <img src={userData?.featured?.[featuredEditIndex] || 'https://placehold.co/150'} style={styles.featPreview} />
                  <button onClick={() => openPhotoSelector('FEATURED_' + featuredEditIndex)} style={styles.changeBtn}>CHANGE PHOTO</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
      <EditProfileModal isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)} userData={userData} triggerPhotoSelector={() => openPhotoSelector('PROFILE')} />
    </div>
  );
};

const styles = {
  layout: { display: 'flex', minHeight: '100vh', background: '#000', color: '#fff' },
  mainContent: { flex: 1, padding: '40px' },
  loader: { height: '100vh', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#D4AF37', fontFamily: 'Orbitron' },
  profileHeader: { display: 'flex', gap: '30px', marginBottom: '30px', borderBottom: '1px solid #222', paddingBottom: '30px' },
  picContainer: { position: 'relative', width: '120px', height: '120px' },
  profilePic: { width: '100%', height: '100%', borderRadius: '50%', border: '2px solid #D4AF37', objectFit: 'cover' },
  editOverlay: { position: 'absolute', top: 0, right: 0, background: '#D4AF37', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: '0.2s', cursor: 'pointer' },
  infoBlock: { display: 'flex', flexDirection: 'column' },
  name: { fontFamily: 'Orbitron', fontSize: '1.8rem', margin: 0 },
  username: { color: '#666' },
  rankBadge: { border: '1px solid #D4AF37', padding: '2px 10px', fontSize: '0.7rem', color: '#D4AF37', width: 'fit-content', marginTop: '10px' },

  featuredRow: { display: 'flex', gap: '15px', marginTop: '15px' },
  featSlotContainer: { position: 'relative' },
  featSlot: { width: '60px', height: '60px', background: '#111', border: '1px solid #222', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  featImg: { width: '100%', height: '100%', objectFit: 'cover' },
  featMenuBtn: { position: 'absolute', top: '-5px', right: '-5px', background: '#000', border: '1px solid #333', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  featDropdown: { position: 'absolute', top: '20px', left: 0, background: '#111', border: '1px solid #333', zIndex: 100, width: '100px' },

  actionRow: { display: 'flex', gap: '15px', marginBottom: '30px' },
  uploadBtn: { background: '#D4AF37', color: '#000', padding: '12px 20px', border: 'none', fontFamily: 'Orbitron', fontWeight: 'bold', cursor: 'pointer' },
  viewLinkBtn: { background: 'transparent', border: '1px solid #D4AF37', color: '#D4AF37', padding: '12px 20px', fontFamily: 'Orbitron', fontWeight: 'bold', cursor: 'pointer' },
  progressLabel: { color: '#D4AF37', fontSize: '0.7rem', marginBottom: '10px' },
  galleryWrapper: { background: '#0a0a0a', border: '1px solid #222', borderRadius: '5px' },
  tabs: { display: 'flex', borderBottom: '1px solid #222' },
  tab: { flex: 1, padding: '15px', background: '#000', color: '#666', border: 'none', fontFamily: 'Orbitron', cursor: 'pointer' },
  activeTab: { flex: 1, padding: '15px', background: '#111', color: '#D4AF37', borderBottom: '2px solid #D4AF37', fontFamily: 'Orbitron' },
  folderContainer: { display: 'flex', padding: '15px 15px 0 15px', gap: '5px' },
  folder: { background: '#111', border: '1px solid #222', borderBottom: 'none', color: '#444', padding: '10px 20px', fontSize: '0.75rem', fontFamily: 'Orbitron', borderRadius: '10px 10px 0 0', cursor: 'pointer' },
  activeFolder: { background: '#1a1a1a', border: '1px solid #D4AF37', borderBottom: '2px solid #1a1a1a', color: '#D4AF37', padding: '10px 20px', fontSize: '0.75rem', fontFamily: 'Orbitron', borderRadius: '10px 10px 0 0', zIndex: 2 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px', padding: '20px', background: '#1a1a1a', borderTop: '1px solid #333' },
  card: { background: '#111', height: '180px', border: '1px solid #333', position: 'relative', overflow: 'hidden' },
  thumb: { width: '100%', height: '100%', objectFit: 'cover' },
  vidThumb: { height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#050505', cursor: 'pointer' },
  menuBtn: { position: 'absolute', top: '5px', right: '5px', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', padding: '5px', cursor: 'pointer', color: '#fff' },
  dropdown: { position: 'absolute', top: '35px', right: '5px', background: '#111', border: '1px solid #333', zIndex: 10, display: 'flex', flexDirection: 'column', width: '160px' },
  dropItem: { background: 'none', border: 'none', color: '#eee', padding: '10px', fontSize: '0.7rem', textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #222', display: 'flex', gap: '5px', alignItems: 'center' },

  // MODALS
  modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.9)', zIndex: 5000, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  selectorWindow: { width: '600px', height: '500px', background: '#000', border: '1px solid #D4AF37', display: 'flex', flexDirection: 'column' },
  selectorGrid: { padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', overflowY: 'auto' },
  selectorThumb: { width: '100%', height: '100px', objectFit: 'cover', border: '1px solid #333', cursor: 'pointer' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', padding: '15px', background: '#111', borderBottom: '1px solid #333', fontFamily: 'Orbitron', color: '#D4AF37' },
  closeIconBtn: { background: 'none', border: 'none', color: '#fff', cursor: 'pointer' },

  featEditWindow: { width: '600px', background: '#000', border: '1px solid #D4AF37' },
  featEditBody: { display: 'flex', padding: '20px', gap: '20px' },
  featEditLeft: { flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' },
  featEditRight: { width: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' },
  titleInput: { background: '#111', border: '1px solid #333', color: '#fff', padding: '10px', marginBottom: '10px', fontFamily: 'Orbitron' },
  textArea: { background: '#111', border: '1px solid #333', color: '#fff', height: '100px', padding: '10px', resize: 'none' },
  label: { color: '#D4AF37', fontFamily: 'Orbitron', fontSize: '0.8rem' },
  saveBtn: { background: '#D4AF37', color: '#000', border: 'none', padding: '10px', fontFamily: 'Orbitron', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  featPreview: { width: '100%', height: '150px', objectFit: 'cover', border: '1px solid #333' },
  changeBtn: { background: '#111', border: '1px solid #D4AF37', color: '#D4AF37', padding: '5px 10px', fontSize: '0.7rem', cursor: 'pointer' },

  playerOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 },
  playerWindow: { background: '#000', border: '1px solid #333', width: '90%', maxWidth: '800px', position: 'relative', padding: '10px' },
  closeBtn: { position: 'absolute', top: '-40px', right: 0, background: 'none', border: 'none', color: '#fff', cursor: 'pointer' },
  playerControls: { padding: '20px' },
  btnRow: { display: 'flex', justifyContent: 'center', gap: '30px', marginTop: '10px' },
  playBtn: { background: '#D4AF37', border: 'none', width: '50px', height: '50px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  navBtn: { background: 'none', border: 'none', color: '#D4AF37', cursor: 'pointer' }
};

export default Profile;
